import { useEffect, useRef, useState } from 'react';
import { BraintreeError, Client, HostedFields, hostedFields } from 'braintree-web';
import { Button, ButtonProps, CircularProgress, Icon, styled, Typography, useTheme } from '@mui/material';
import { Alert } from '@/components/DynamicUI/Alert';
import { AlertSeverity } from '@/constants/alertTypes';
import { ControllerRenderProps, FieldError } from 'react-hook-form';
import useAnalytics from '@/hooks/use-analytics';
import { InertIcon } from '../InertIcon';
import { IconColors, IconSizes } from '@/constants/iconTypes';

interface BraintreeCreditCardProps {
  onSuccess: (nonce: string) => Promise<void>;
  clientInstance: Client;
  legal: string;
  amount: string;
  fieldProps: ControllerRenderProps;
  error: FieldError | undefined;
}

interface BraintreeErrorDetails {
  invalidFieldKeys: string[];
  invalidFields: Record<string, HTMLDivElement>;
}

interface ExtendedBraintreeError extends BraintreeError {
  details: BraintreeErrorDetails;
}

export function BraintreeCreditCardForm({ onSuccess, clientInstance, amount, legal, error }: BraintreeCreditCardProps) {
  const creditCards = import.meta.glob('/src/assets/logos/creditCards/*.svg', {
    eager: true,
    query: '?url',
    import: 'default',
  });

  const hostedFieldsInstance = useRef<HostedFields | null>(null);
  const hasSetupInitialized = useRef(false);
  const cardHolderName = useRef<HTMLDivElement>(null);
  const cardNumber = useRef<HTMLDivElement>(null);
  const expirationDate = useRef<HTMLDivElement>(null);
  const cvv = useRef<HTMLDivElement>(null);
  const postalCode = useRef<HTMLDivElement>(null);

  const fieldRefs = {
    cardHolderName,
    cardNumber,
    expirationDate,
    cvv,
    postalCode,
  };

  const [showLoadingState, setShowLoadingState] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errors, setErrors] = useState<{ message: string | null; details: BraintreeErrorDetails | null } | null>(null);
  const { trackEvent } = useAnalytics();
  const theme = useTheme();

  // Initialize Braintree Hosted Fields
  useEffect(() => {
    async function setupBraintree() {
      try {
        const fields = await hostedFields.create({
          client: clientInstance,
          styles: {
            input: {
              color: theme.palette.text.primary,
              'font-family': '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;',
              'font-size': '16px',
            },
            '::placeholder': {
              color: theme.palette.text.secondary,
            },
          },
          fields: {
            cardholderName: {
              selector: '#card-holder-name',
              placeholder: 'Cardholder name',
            },
            number: {
              selector: '#card-number',
              placeholder: 'Card number',
              maxCardLength: 16,
            },
            cvv: {
              selector: '#cvv',
              placeholder: 'CVV',
            },
            expirationDate: {
              selector: '#expiration-date',
              placeholder: 'Expiration date',
            },
            postalCode: {
              selector: '#postal-code',
              placeholder: 'ZIP code',
            },
          },
        });

        hostedFieldsInstance.current = fields;
        setShowLoadingState(false);
      } catch (error) {
        reportError({ error });
      }
    }

    if (hasSetupInitialized.current) return;
    hasSetupInitialized.current = true;
    void setupBraintree();
  }, [clientInstance, theme]);

  useEffect(() => {
    if (error?.message) {
      setErrors({ message: error.message, details: null });
    }
  }, [error]);

  // Handle form submission and tokenize the card details
  async function addCard(): Promise<void> {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      if (errors?.message) {
        setErrors(null);
      }
      const payload = await hostedFieldsInstance.current?.tokenize();
      if (payload) {
        trackEvent('BRAINTREE_CREDIT_CARD_PAYMENT_AUTHORIZED');
        await onSuccess(payload.nonce);
      }
    } catch (error: unknown) {
      trackEvent('BRAINTREE_ADDING_CREDIT_CARD_FAILED');
      if (error && typeof error === 'object' && 'name' in error && error.name === 'BraintreeError') {
        if ((error as ExtendedBraintreeError).code) {
          const braintreeErr = error as ExtendedBraintreeError;
          switch (braintreeErr.code) {
            case 'HOSTED_FIELDS_FIELDS_EMPTY':
              setErrors({ message: 'All fields are empty! Please fill out the form.', details: braintreeErr.details });
              break;
            case 'HOSTED_FIELDS_FIELDS_INVALID':
              setErrors({ message: null, details: braintreeErr.details });
              break;
            case 'HOSTED_FIELDS_FAILED_TOKENIZATION':
              setErrors({
                message: 'Tokenization failed server side. Is the card valid?',
                details: null,
              });
              break;
            case 'HOSTED_FIELDS_TOKENIZATION_NETWORK_ERROR':
              console.error('Network error occurred when tokenizing.');
              setErrors({
                message: 'Network error occurred. Please try again.',
                details: null,
              });
              break;
            default:
              setErrors({
                message: 'Payment failed. Please try again.',
                details: null,
              });
          }
        }
      } else {
        reportError({ error });
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  // Clear field-specific errors when the user corrects them
  hostedFieldsInstance.current?.on('validityChange', (event) => {
    const field = event.emittedBy;
    const isValid = event.fields[field].isValid;
    if (errors?.details?.invalidFieldKeys.includes(field) && isValid) {
      const invalidFieldKeys = errors.details.invalidFieldKeys.filter((key) => key !== field);
      setErrors({
        message: errors.message,
        details: {
          invalidFieldKeys,
          invalidFields: Object.fromEntries(
            Object.entries(errors.details.invalidFields).filter(([key]) => key !== field)
          ),
        },
      });
    }
  });

  return (
    <>
      <PaymentHeader>
        <Typography
          color={theme.palette.text.secondary}
          fontFamily={theme.typography.fontFamily}
          fontSize={theme.typography.body1.fontSize}
          fontWeight={500}
        >
          Credit/Debit
        </Typography>
        <CreditCards>
          {Object.entries(creditCards).map(([path, url]) => (
            <img
              key={path}
              src={url as string}
              alt={path.split('/').pop()}
            />
          ))}
        </CreditCards>
      </PaymentHeader>
      {errors?.message && (
        <Alert
          severity={AlertSeverity.ERROR}
          titleGutterBottom={false}
          title='Payment Error'
          text={typeof errors.message === 'string' ? errors.message : 'An error occurred.'}
        />
      )}
      {showLoadingState && (
        <LoaderContainer>
          <CircularProgress color='primary' />
        </LoaderContainer>
      )}
      <StyledContainer isLoading={showLoadingState}>
        <HostedField
          id='card-holder-name'
          name='cardholderName'
          errors={errors}
          ref={fieldRefs.cardHolderName}
        />
        <HostedField
          id='card-number'
          name='number'
          errors={errors}
          ref={fieldRefs.cardNumber}
        />
        <HostedField
          id='expiration-date'
          name='expirationDate'
          errors={errors}
          ref={fieldRefs.expirationDate}
        />
        <HostedField
          id='cvv'
          name='cvv'
          errors={errors}
          ref={fieldRefs.cvv}
        >
          <StyledIcon>
            <InertIcon
              icon_name='payment'
              size={IconSizes.SMALL}
              color={IconColors.TEXT}
            />
          </StyledIcon>
        </HostedField>
        <HostedField
          id='postal-code'
          name='postalCode'
          errors={errors}
          ref={fieldRefs.postalCode}
        />
        <Typography
          color={theme.palette.text.secondary}
          fontSize={theme.typography.body2.fontSize}
          style={{ gridColumn: '1 / -1', marginBottom: '0.5rem' }}
        >
          {legal}
        </Typography>
        <StyledButton
          onClick={() => {
            void addCard();
          }}
          disabled={isSubmitting}
          loading={isSubmitting}
          fullWidth={true}
          variant={'contained'}
          role='submit'
          size={'large'}
          startIcon={<Icon>credit_card</Icon>}
          color={'primary'}
        >
          Submit payment of ${amount}
        </StyledButton>
      </StyledContainer>
    </>
  );
}

interface StyledFormProps {
  isLoading: boolean;
}

interface HostedFieldProps {
  name: string;
  errors: { message: string | null; details: BraintreeErrorDetails | null } | null;
}

const StyledContainer = styled('div')<StyledFormProps>`
  visibility: ${({ isLoading }) => (isLoading ? 'hidden' : 'visible')};
  height: ${({ isLoading }) => (isLoading ? '0' : 'auto')};
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  margin-top: 16px;

  & > :nth-of-type(1),
  & > :nth-of-type(2),
  & > :nth-of-type(5),
  & > :nth-of-type(6) {
    grid-column: 1 / -1;
  }
`;

const HostedField = styled('div')<HostedFieldProps>`
  position: relative;
  outline-style: solid;
  outline-color: ${({ errors, name, theme }) =>
    errors?.details?.invalidFieldKeys?.includes(name) ? theme.palette.error.main : theme.palette.border.main};
  outline-width: ${({ errors, name }) => (errors?.details?.invalidFieldKeys?.includes(name) ? '2px' : '1px')};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  height: 3.3125rem;
  padding: 0 14px;
  margin-bottom: ${({ errors, name }) => (errors?.details?.invalidFieldKeys?.includes(name) ? '1.3125rem' : undefined)};

  ${({ errors, name, theme }) =>
    !errors?.details?.invalidFieldKeys?.includes(name) &&
    `
      &:hover {
        outline-color: ${theme.palette.text.primary};
      }

    `}

  &.braintree-hosted-fields-focused {
    outline-color: ${({ theme }) => theme.palette.tertiary.main};
    outline-width: 2px;
  }

  &::before {
    content: ${({ errors, name }) => (errors?.details?.invalidFieldKeys?.includes(name) ? '"Invalid Format"' : '')};
    font-size: 16px;
    color: ${({ theme }) => theme.palette.error.main};
    position: absolute;
    bottom: -1.5rem;
    left: 0.75rem;
  }
`;

const LoaderContainer = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 56px; // Height to match the form when loading
`;

const StyledButton = styled(Button)<ButtonProps>`
  background-color: ${({ theme }) => theme.palette.primary.main};
  & .MuiTouchRipple-root {
    color: ${({ theme }) => theme.palette.primary.main};
  }
`;

const PaymentHeader = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 0 4px;
`;

const CreditCards = styled('div')`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const StyledIcon = styled('div')`
  display: flex;
  align-items: center;
  position: absolute;
  height: 100%;
  right: 14px;
  top: 0;
  bottom: 0;
`;
